import { Injectable } from '@angular/core';
import { AuthService } from '../../../../core';
import { isTipoPromocaoSemPromocao } from '../../promocao/tipo';
import { isStatusEmAnalise, isStatusEmAndamento, isStatusEncerrada, isStatusNaoConfirmada, isStatusReprovada } from '../status';

/** Service que valida os acessos e execuções de funcionalidades das campanhas */
@Injectable({
    providedIn: 'root'
})
export class CampanhaAuthGuardService {

    constructor(private authService: AuthService) { }

    /** Verifica se pode editar uma campanha */
    canEdit(idCampanha: number, statusCampanha: number, idUsuarioCreated: number): boolean {
        // Se a campanha não possuir ID então não pode editar
        if (idCampanha <= 0) {
            return false;
        }

        // Se a campanha estiver encerrada ou reprovada então não pode editar
        if (isStatusEncerrada(statusCampanha) || isStatusReprovada(statusCampanha)) {
            return false;
        }

        // Se não, somente o usuário que criou a campanha pode editar
        if (isStatusNaoConfirmada(statusCampanha) || (isStatusEmAnalise(statusCampanha)) && (idUsuarioCreated === this.authService.getIdUsuario())) {
            return true
        }

        // Se for um admin ou atendente então pode editar
        return this.authService.isAdministrador() || this.authService.isAtendente() || this.authService.isFinanceiro();
    }

    /** Verifica se pode confirmar o status da campanha */
    canConfirmarStatus(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, editMode: boolean): boolean {
        // Se estiver inserindo
        if (!editMode) {
            return true;
        }

        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

        // Se estiver editando uma campanha não confirmada
        if (isStatusNaoConfirmada(statusCampanha)) {
            return true;
        }

        return false;
    }

    /** Verifica se pode editar a área da campanha */
    canEditArea(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, editMode: boolean): boolean {
        // Se estiver inserindo
        if (!editMode) {
            return true;
        }

        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

       return !editMode;
    }

    /** Verifica se pode exibir a promoção da campanha */
    canShowPromocao(tipoPromocaoPacote: number): boolean {
        if (!tipoPromocaoPacote) {
            return true;
        }

        /** Somente exibe a promoção quando estiver configurado algum tipo de promoção no pacote, ou seja, quando o pacote não determinar que não há promoção */
        return !isTipoPromocaoSemPromocao(tipoPromocaoPacote);
    }

    /** Verifica se pode editar a promoção da campanha */
    canEditPromocao(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, editMode: boolean): boolean {
        // Se estiver inserindo
        if (!editMode) {
            return true;
        }

        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

        // Se estiver editando uma campanha não confirmada ou em análise ou em andamento
        if (isStatusNaoConfirmada(statusCampanha) || isStatusEmAnalise(statusCampanha) || isStatusEmAndamento(statusCampanha)) {
            return true;
        }

        return false;
    }

    /** Verifica se pode editar o tipo da promoção da campanha */
    canEditTipoPromocao(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, editMode: boolean, tipoPromocaoCampanha: number, tipoPromocaoPacote: number): boolean {
        // Se não puder editar a promoção da campanha em sí, então também não pode editar o tipo de promoção
        if (!this.canEditPromocao(idCampanha, statusCampanha, idUsuarioCreated, editMode)) {
            return false;
        }

        // Somente pode editar o tipo se o mesmo não estiver determinado no pacote da campanha ou se for diferente do tipo da campanha
        return !tipoPromocaoPacote || (tipoPromocaoPacote != tipoPromocaoCampanha);
    }

    /** Verifica se pode editar a flag de vigência recorrente da campanha */
    canEditVigenciaRecorrente(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, vigenciaRecorrentePacote: boolean): boolean {
        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

        // Se a campanha estiver não confirmada ou em análise
        if (!isStatusNaoConfirmada(statusCampanha) && !isStatusEmAnalise(statusCampanha)) {
            return false;
        }

        // Somente pode editar se o pacote não possuir a flag da vigência recorrente informada explicitamente
        return vigenciaRecorrentePacote == null || vigenciaRecorrentePacote === undefined;
    }

    /** Verifica se pode editar a quantidade de dias da vigência da campanha */
    canEditQuantidadeDiasVigencia(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, quantidadeDiasVigencia: number): boolean {
        return false;
        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

        // Se a campanha estiver não confirmada ou em análise
        if (!isStatusNaoConfirmada(statusCampanha) && !isStatusEmAnalise(statusCampanha)) {
            return false;
        }

        // Somente pode editar se o pacote não possuir a quantidade de dias de vigência informada explicitamente
        return quantidadeDiasVigencia == null || quantidadeDiasVigencia === undefined;
    }

    /** Verifica se pode editar a tabela de preço da campanha */
    canEditTabelaPreco(idCampanha: number, statusCampanha: number, idUsuarioCreated: number, quantidadeTabelasPrecosPacote: number): boolean {
        // Se não puder editar
        if (!this.canEdit(idCampanha, statusCampanha, idUsuarioCreated)) {
            return false;
        }

        // Somente pode editar se o pacote não possuir tabela de preço informada explicitamente ou se a tabela for diferente da atualmente selecionada na campanha
        return !quantidadeTabelasPrecosPacote || quantidadeTabelasPrecosPacote === 0;
    }

    /** Verifica se pode visualizar a campanha */
    canVisualizar(): boolean {
        return true;
    }

    /** Verifica se pode excluir uma campanha */
    canDelete(idCampanha: number, statusCampanha: number, quantidadePedidosVendasNaoLiberados: number, quantidadePedidosBonificacoesNaoLiberados: number, idUsuarioCreated: number): boolean {
        // Se a campanha não possuir ID então não pode excluir
        if (idCampanha <= 0) {
            return false;
        }

        // Se a campanha estiver encerrada ou reprovada então não pode excluir
        if (isStatusEncerrada(statusCampanha) || isStatusReprovada(statusCampanha)) {
            return false;
        }

        // Se possuir algum pedido não liberado então não pode excluir por que a campanha está em análise apenas para resolver as pendências de pedidos não liberados
        if (quantidadePedidosVendasNaoLiberados > 0 || quantidadePedidosBonificacoesNaoLiberados > 0) {
            return false;
        }

        // Se for um admin ou atendente então pode excluir
        if (this.authService.isAdministrador() || this.authService.isAtendente()) {
            return true;
        }

        // Se não, somente se a campanha estiver Não Confirmar e for o usuário que criou a campanha
        return isStatusNaoConfirmada(statusCampanha) && (idUsuarioCreated === this.authService.getIdUsuario());
    }

    /** Verifica se pode exibir os representantes de uma campanha */
    canShowRepresentante(): boolean {
       // Somente admins e atendentes
       return this.authService.isAdministrador() || this.authService.isAtendente() || this.authService.isFinanceiro();
    }
}
